import { forwardRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as RTabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import { AlertTriangle, Check, ChevronDown, Copy, Inbox, Loader2, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../lib/api';

export const cx = clsx;

/* ─────────────────────────────── Buttons ─────────────────────────────── */
type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dark';
export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: 'sm' | 'md'; loading?: boolean; icon?: ReactNode }>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        'inline-flex select-none items-center justify-center gap-1.5 rounded-lg font-medium whitespace-nowrap transition-[background,box-shadow,color,border] duration-150 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:shrink-0',
        size === 'sm' ? 'h-8 px-2.5 text-[13px]' : 'h-9 px-3.5 text-[13.5px]',
        variant === 'primary' && 'bg-accent-600 text-white shadow-btn hover:bg-accent-700 active:bg-accent-700',
        variant === 'secondary' && 'border border-line-strong bg-white text-slate-800 shadow-card hover:border-slate-300 hover:bg-slate-50',
        variant === 'ghost' && 'text-slate-600 hover:bg-slate-900/[0.05] hover:text-slate-900',
        variant === 'danger' && 'bg-red-600 text-white shadow-btn hover:bg-red-700',
        variant === 'dark' && 'bg-slate-900 text-white shadow-btn hover:bg-slate-800',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

/* ─────────────────────────────── Form controls ─────────────────────────────── */
const control = 'w-full rounded-lg border border-line-strong bg-white text-[13.5px] text-ink shadow-card placeholder:text-slate-400 transition-[border,box-shadow] duration-150 hover:border-slate-300 focus:border-accent-500 focus:outline-none focus:ring-4 focus:ring-accent-100 disabled:bg-slate-50 disabled:text-slate-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(control, 'h-9 px-3', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(control, 'min-h-[88px] px-3 py-2 leading-relaxed', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <span className={cx('relative inline-flex', className?.includes('w-full') && 'w-full')}>
      <select ref={ref} className={cx(control, 'h-9 appearance-none pr-8 pl-2.5', className)} {...rest}>
        {children}
      </select>
      <ChevronDown aria-hidden className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-slate-400" />
    </span>
  );
});

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-slate-700">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[12px] text-red-600" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12px] text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────── Surfaces ─────────────────────────────── */
export function Card({ children, className, title, actions, padded = true, subtitle }: { children: ReactNode; className?: string; title?: ReactNode; actions?: ReactNode; padded?: boolean; subtitle?: ReactNode }) {
  return (
    <section className={cx('overflow-hidden rounded-xl border border-line bg-white shadow-card', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[12px] text-slate-500">{subtitle}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        </header>
      )}
      <div className={padded ? 'p-4' : ''}>{children}</div>
    </section>
  );
}

/* ─────────────────────────────── Badges & pills ─────────────────────────────── */
const TONES: Record<string, string> = {
  slate: 'bg-slate-100 text-slate-700 ring-slate-200/80',
  blue: 'bg-sky-50 text-sky-800 ring-sky-200/80',
  indigo: 'bg-accent-50 text-accent-700 ring-accent-200/70',
  green: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200/80',
  red: 'bg-rose-50 text-rose-700 ring-rose-200/80',
  cyan: 'bg-teal-50 text-teal-800 ring-teal-200/80',
  yellow: 'bg-yellow-50 text-yellow-800 ring-yellow-300/80',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200/80',
};
const DOTS: Record<string, string> = { slate: 'bg-slate-400', blue: 'bg-sky-500', indigo: 'bg-accent-500', green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-rose-500', cyan: 'bg-teal-500', yellow: 'bg-yellow-500', violet: 'bg-violet-500' };

export function Badge({ tone = 'slate', children, className, dot }: { tone?: keyof typeof TONES | string; children: ReactNode; className?: string; dot?: boolean }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[11.5px] leading-none font-medium whitespace-nowrap ring-1 ring-inset', TONES[tone] ?? TONES.slate, className)}>
      {dot && <span aria-hidden className={cx('size-1.5 rounded-full', DOTS[tone] ?? DOTS.slate)} />}
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, string> = {
  open: 'blue',
  in_progress: 'indigo',
  ready_for_review: 'amber',
  resolved: 'green',
  reopened: 'red',
  draft: 'slate',
  in_review: 'blue',
  changes_requested: 'amber',
  approved: 'green',
  pending: 'blue',
  completed: 'green',
  superseded: 'slate',
  not_connected: 'slate',
  connected: 'green',
  authorization_required: 'amber',
  connection_failed: 'red',
  published: 'green',
  queued: 'slate',
  processing: 'indigo',
  ready: 'green',
  failed: 'red',
};
export function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <Badge tone={STATUS_TONE[status] ?? 'slate'} dot>
      {t(`status.${status}`, { defaultValue: status.replace(/_/g, ' ') })}
    </Badge>
  );
}

export function PriorityPill({ priority }: { priority: string }) {
  const bars = { low: 1, medium: 2, high: 3, critical: 4 }[priority] ?? 1;
  const tone = priority === 'critical' ? 'text-rose-600' : priority === 'high' ? 'text-amber-600' : 'text-slate-500';
  return (
    <span className={cx('inline-flex items-center gap-1 text-[11.5px] font-medium', tone)} title={`${priority} priority`}>
      <span aria-hidden className="flex items-end gap-[2px]">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={cx('w-[3px] rounded-sm', i <= bars ? 'bg-current' : 'bg-slate-200')} style={{ height: 3 + i * 2 }} />
        ))}
      </span>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
}

export function CategoryChip({ category }: { category: string }) {
  return <Badge tone={category === 'qa' ? 'indigo' : category === 'discussion' ? 'violet' : 'cyan'}>{category === 'qa' ? 'QA' : category === 'discussion' ? 'Discussion' : 'Suggestion'}</Badge>;
}

/* ─────────────────────────────── Feedback states ─────────────────────────────── */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-lg bg-slate-200/60', className)} />;
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({ title, body, action, icon }: { title: string; body?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line-strong bg-white/60 px-6 py-12 text-center">
      <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-white text-slate-400 shadow-raise ring-1 ring-line">{icon ?? <Inbox className="size-5" />}</div>
      <p className="text-[14px] font-semibold text-ink">{title}</p>
      {body && <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-slate-500">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/70 p-4 text-sm text-rose-900">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1">
        <p className="font-semibold">{title ?? 'Could not load this'}</p>
        <p className="mt-0.5 text-rose-800">{errorMessage(error)}</p>
      </div>
      {onRetry && (
        <Button size="sm" variant="secondary" onClick={onRetry} icon={<RefreshCw className="size-3.5" />}>
          Retry
        </Button>
      )}
    </div>
  );
}

export function Notice({ tone = 'amber', children, icon }: { tone?: 'amber' | 'blue' | 'red' | 'green' | 'slate'; children: ReactNode; icon?: ReactNode }) {
  const c = { amber: 'border-amber-200 bg-amber-50/80 text-amber-900', blue: 'border-sky-200 bg-sky-50/80 text-sky-900', red: 'border-rose-200 bg-rose-50/80 text-rose-900', green: 'border-emerald-200 bg-emerald-50/80 text-emerald-900', slate: 'border-line bg-slate-50 text-slate-700' }[tone];
  return (
    <div className={cx('flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-[13px] leading-relaxed', c)}>
      {icon ?? <AlertTriangle className="mt-[3px] size-3.5 shrink-0" />}
      <div className="flex-1">{children}</div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cx('size-4 animate-spin text-slate-400', className)} aria-label="Loading" />;
}

export function FullPageSpinner() {
  return (
    <div className="flex h-full min-h-[50vh] items-center justify-center">
      <Spinner className="size-6" />
    </div>
  );
}

/* ─────────────────────────────── Page structure ─────────────────────────────── */
export function PageHeader({ title, subtitle, actions, breadcrumbs }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; breadcrumbs?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {breadcrumbs && <div className="mb-1.5 flex items-center gap-1 text-[12.5px] text-slate-500">{breadcrumbs}</div>}
        <h1 className="truncate text-[22px] leading-tight font-semibold tracking-[-0.02em] text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-[13.5px] text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({ open, onOpenChange, title, description, children, footer, size = 'md' }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: ReactNode; children?: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 animate-fade-in bg-slate-950/40 backdrop-blur-[2px]" />
        <Dialog.Content className={cx('fixed top-1/2 left-1/2 z-50 max-h-[90vh] w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2 animate-pop-in overflow-auto rounded-2xl bg-white shadow-pop ring-1 ring-line focus:outline-none', size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-3xl' : 'max-w-lg')}>
          <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
            <div>
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.01em] text-ink">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-1 text-[13px] leading-relaxed text-slate-500">{description}</Dialog.Description> : <Dialog.Description className="sr-only">{title}</Dialog.Description>}
            </div>
            <Dialog.Close className="-mt-1 -mr-2 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
              <X className="size-4" />
            </Dialog.Close>
          </div>
          <div className="px-6 pb-5">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-line bg-slate-50/70 px-6 py-3.5">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// count: a number shown next to the label (alert = shown in red).
export function Tabs({ value, onValueChange, tabs, children }: { value: string; onValueChange: (v: string) => void; tabs: { value: string; label: ReactNode; count?: number; countTone?: 'alert' }[]; children: ReactNode }) {
  return (
    <RTabs.Root value={value} onValueChange={onValueChange}>
      <RTabs.List className="scrollbar-thin mb-6 flex gap-1 overflow-x-auto rounded-2xl border border-line bg-white p-1.5 shadow-card" aria-label="Sections">
        {tabs.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-medium whitespace-nowrap text-slate-600 transition-colors hover:bg-slate-50 hover:text-ink data-[state=active]:bg-accent-50 data-[state=active]:text-accent-800 data-[state=active]:ring-1 data-[state=active]:ring-accent-200/70 [&_svg]:size-4 [&_svg]:text-slate-400 data-[state=active]:[&_svg]:text-accent-600"
          >
            {t.label}
            {t.count != null && t.count > 0 && <span className={cx('min-w-[18px] rounded-full px-1.5 text-center text-[11px] leading-[18px] font-semibold tabular-nums', t.countTone === 'alert' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600')}>{t.count > 99 ? '99+' : t.count}</span>}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {children}
    </RTabs.Root>
  );
}
export const TabPanel = RTabs.Content;

const AVATAR_BG = ['#4f46e5', '#0f766e', '#b45309', '#be123c', '#7c3aed', '#0369a1', '#15803d', '#a21caf'];
export function Avatar({ name, size = 28 }: { name?: string | null; size?: number }) {
  const n = (name ?? '?').trim();
  const initials = n.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const bg = AVATAR_BG[[...n].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_BG.length];
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-2 ring-white" style={{ width: size, height: size, fontSize: Math.max(10, size * 0.38), background: bg }} aria-hidden="true">
      {initials || '?'}
    </span>
  );
}

export function CopyField({ value, label, mono = true, multiline = false }: { value: string; label?: string; mono?: boolean; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-start gap-2">
      {multiline ? (
        <pre aria-label={label} className={cx('scrollbar-thin max-h-48 flex-1 overflow-auto rounded-lg border border-line bg-slate-950 p-3 text-[12px] leading-relaxed break-all whitespace-pre-wrap text-slate-200', mono && 'font-mono')}>
          {value}
        </pre>
      ) : (
        <input readOnly aria-label={label} value={value} className={cx('h-9 min-w-0 flex-1 rounded-lg border border-line bg-slate-50 px-3 text-[12.5px] text-slate-700', mono && 'font-mono')} onFocus={(e) => e.currentTarget.select()} />
      )}
      <Button
        size="sm"
        variant="secondary"
        icon={copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
        onClick={async () => {
          await navigator.clipboard.writeText(value).catch(() => undefined);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

export function Stat({ label, value, tone, hint, icon }: { label: string; value: ReactNode; tone?: 'amber' | 'red' | 'green'; hint?: ReactNode; icon?: ReactNode }) {
  const chip = tone === 'amber' ? 'bg-amber-50 text-amber-600 ring-amber-200/70' : tone === 'red' ? 'bg-rose-50 text-rose-600 ring-rose-200/70' : tone === 'green' ? 'bg-emerald-50 text-emerald-600 ring-emerald-200/70' : 'bg-accent-50 text-accent-600 ring-accent-200/60';
  return (
    <div className="rounded-xl border border-line bg-white p-4 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12.5px] font-medium text-slate-500">{label}</p>
        {icon && <span className={cx('flex size-7 items-center justify-center rounded-lg ring-1 [&_svg]:size-3.5', chip)}>{icon}</span>}
      </div>
      <p className={cx('tabular mt-2 text-[26px] leading-none font-semibold tracking-[-0.02em]', tone === 'amber' ? 'text-amber-700' : tone === 'red' ? 'text-rose-700' : tone === 'green' ? 'text-emerald-700' : 'text-ink')}>{value}</p>
      {hint && <p className="mt-1.5 text-[12px] text-slate-500">{hint}</p>}
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, body, confirmLabel, onConfirm, danger, loading }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; body: ReactNode; confirmLabel: string; onConfirm: () => void; danger?: boolean; loading?: boolean }) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-[13.5px] leading-relaxed text-slate-700">{body}</div>
    </Modal>
  );
}

/** An on/off switch with its label and an optional explanation underneath. */
export function Switch({ checked, onChange, label, hint, id }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; id: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <label htmlFor={id} className="min-w-0 cursor-pointer">
        <span className="block text-[13.5px] font-medium text-slate-900">{label}</span>
        {hint && <span className="mt-0.5 block text-[12.5px] leading-relaxed text-slate-500">{hint}</span>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cx('relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-150', checked ? 'bg-accent-600' : 'bg-slate-300')}
      >
        <span aria-hidden className={cx('inline-block size-5 rounded-full bg-white shadow-card ring-1 ring-slate-900/5 transition-transform duration-150', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
      </button>
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; title?: string }[]; label: string }) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg bg-slate-900/[0.05] p-[3px] ring-1 ring-inset ring-slate-900/[0.04]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx('flex h-7 items-center rounded-md px-2.5 text-[12.5px] font-medium transition-all duration-150', value === o.value ? 'bg-white text-ink shadow-card ring-1 ring-slate-900/[0.06]' : 'text-slate-500 hover:text-ink')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
